// 정책/설정 store — 정책 편집(추가/수정/삭제), Webhook 시크릿, 서버 DB 영속화 (전역 검사/격리/재시도 설정은 시스템 설정 탭·서버 저장)
import { create } from 'zustand';
import type { Policy } from './policies';
import { generateWebhookSecret } from './secret';
import { adminPut, adminDelete } from './adminApi';

export type { Policy };

export interface Settings {
  webhookSecret: string;
}

export const DEFAULT_POLICIES: Record<string, Policy> = {
  default: {
    name: '기본 정책',
    maxSizeMB: 500,
    maxFiles: 10,
    allowedExt: ['.pdf', '.docx', '.xlsx', '.pptx', '.jpg', '.jpeg', '.png', '.gif', '.mp4', '.mov', '.zip', '.csv', '.txt'],
    blockedExt: ['.exe', '.bat', '.ps1', '.sh', '.vbs', '.jar', '.dll', '.msi', '.scr', '.cmd', '.hta'],
    engines: ['clamav', 'yara'],
    scanTimeoutS: 30,
    quarantineMacro: true,
    quarantinePdfJs: true,
  },
  strict: {
    name: '엄격 정책',
    maxSizeMB: 50,
    maxFiles: 1,
    allowedExt: ['.pdf', '.jpg', '.jpeg', '.png'],
    blockedExt: ['.exe', '.bat', '.ps1', '.sh', '.vbs', '.jar', '.dll', '.msi', '.scr', '.cmd', '.hta', '.zip', '.rar'],
    engines: ['clamav', 'yara'],
    scanTimeoutS: 30,
    quarantineMacro: true,
    quarantinePdfJs: true,
  },
  'media-only': {
    name: '미디어 정책',
    maxSizeMB: 2048,
    maxFiles: 5,
    allowedExt: ['.jpg', '.jpeg', '.png', '.gif', '.mp4', '.mov', '.webp'],
    blockedExt: ['.exe', '.bat', '.ps1', '.sh', '.vbs', '.jar', '.dll', '.msi', '.scr', '.cmd', '.hta'],
    engines: ['clamav'],
    scanTimeoutS: 30,
    quarantineMacro: true,
    quarantinePdfJs: true,
  },
};

export const DEFAULT_SETTINGS: Settings = {
  webhookSecret: '',
};

const POLICIES_KEY = 'fg_policies';
const VALID_ID_RE = /^[a-z0-9][a-z0-9_-]*$/i;
const ADMIN_TOKEN = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_ADMIN_TOKEN ?? '';

// DB 행 → UI Policy 변환
interface DbPolicy {
  id: string; name: string; maxSizeMb: number; maxFiles: number;
  allowedExt: string[] | null; blockedExt: string[] | null; engines: string[] | null;
  scanTimeoutS?: number | null; quarantineMacro?: boolean | null; quarantinePdfJs?: boolean | null;
}
function dbToUi(row: DbPolicy): [string, Policy] {
  return [row.id, {
    name: row.name,
    maxSizeMB: row.maxSizeMb,
    maxFiles: row.maxFiles,
    allowedExt: row.allowedExt ?? [],
    blockedExt: row.blockedExt ?? [],
    engines: row.engines ?? ['clamav', 'yara'],
    scanTimeoutS: row.scanTimeoutS ?? 30,
    quarantineMacro: row.quarantineMacro ?? true,
    quarantinePdfJs: row.quarantinePdfJs ?? true,
  }];
}

// 실패(400 등) 시 서버 message 를 담은 AdminApiError 를 던진다
async function apiPut(id: string, p: Policy): Promise<void> {
  await adminPut(`/admin/policies/${id}`, {
    name: p.name, maxSizeMb: p.maxSizeMB, maxFiles: p.maxFiles, allowedExt: p.allowedExt, blockedExt: p.blockedExt, engines: p.engines,
    scanTimeoutS: p.scanTimeoutS, quarantineMacro: p.quarantineMacro, quarantinePdfJs: p.quarantinePdfJs,
  });
}

async function apiDelete(id: string): Promise<void> {
  await adminDelete(`/admin/policies/${id}`);
}

async function apiFetchAll(): Promise<Record<string, Policy>> {
  const res = await fetch('/admin/policies', { headers: { 'X-Admin-Token': ADMIN_TOKEN } });
  if (!res.ok) throw new Error('정책 로딩 실패');
  const data = await res.json() as { items: DbPolicy[] };
  if (data.items.length === 0) return {};
  return Object.fromEntries(data.items.map(dbToUi));
}

function readFromStorage(): { policies: Record<string, Policy>; settings: Settings } | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(POLICIES_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as { policies: Record<string, Policy>; settings: Settings };
  } catch { return null; }
}

function writeToStorage(policies: Record<string, Policy>, settings: Settings) {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(POLICIES_KEY, JSON.stringify({ policies, settings }));
}

interface PolicyState {
  policies: Record<string, Policy>;
  settings: Settings;
  selectedId: string;
  dirty: boolean;
  selectPolicy: (id: string) => void;
  updatePolicy: (id: string, patch: Partial<Policy>) => void;
  updateSettings: (patch: Partial<Settings>) => void;
  regenerateWebhookSecret: () => void;
  ensureWebhookSecret: () => void;
  addPolicy: (id: string, p: Policy) => void;
  deletePolicy: (id: string) => void;
  save: () => Promise<void>;
  resetToDefaults: () => void;
  loadFromStorage: () => void;
  loadFromServer: () => Promise<void>;
  extToString: (arr: string[]) => string;
  stringToExt: (s: string) => string[];
}

const _stored = readFromStorage();

export const usePolicyStore = create<PolicyState>((set, get) => ({
  policies: _stored?.policies ?? structuredClone(DEFAULT_POLICIES),
  settings: _stored?.settings ?? structuredClone(DEFAULT_SETTINGS),
  selectedId: 'default',
  dirty: false,

  selectPolicy: (id) => set({ selectedId: id }),

  updatePolicy: (id, patch) =>
    set((s) => {
      const target = s.policies[id];
      if (!target) return s;
      return { policies: { ...s.policies, [id]: { ...target, ...patch } }, dirty: true };
    }),

  updateSettings: (patch) => {
    set((s) => {
      const next = { ...s.settings, ...patch };
      writeToStorage(s.policies, next);
      return { settings: next, dirty: true };
    });
  },

  regenerateWebhookSecret: () =>
    set((s) => {
      const next = { ...s.settings, webhookSecret: generateWebhookSecret() };
      writeToStorage(s.policies, next);
      return { settings: next, dirty: true };
    }),

  ensureWebhookSecret: () => {
    const s = get();
    if (!s.settings.webhookSecret) {
      const next = { ...s.settings, webhookSecret: generateWebhookSecret() };
      writeToStorage(s.policies, next);
      set({ settings: next });
    }
  },

  addPolicy: (id, p) => {
    if (!VALID_ID_RE.test(id)) throw new Error('정책 ID는 영문/숫자/언더바/하이픈만 가능합니다');
    const s = get();
    if (s.policies[id]) throw new Error(`이미 존재하는 정책 ID: ${id}`);
    set({ policies: { ...s.policies, [id]: p }, selectedId: id, dirty: true });
  },

  deletePolicy: (id) => {
    const s = get();
    if (!s.policies[id]) return;
    if (Object.keys(s.policies).length <= 1) throw new Error('최소 1개의 정책은 유지되어야 합니다');
    apiDelete(id).catch(() => undefined);
    const next = { ...s.policies };
    delete next[id];
    set({ policies: next, selectedId: s.selectedId === id ? (Object.keys(next)[0] ?? 'default') : s.selectedId, dirty: true });
  },

  save: async () => {
    const s = get();
    writeToStorage(s.policies, s.settings);
    // 서버 저장 실패(400 등) 시 reject — dirty 유지 (로컬 저장은 완료)
    await Promise.all(Object.entries(s.policies).map(([id, p]) => apiPut(id, p)));
    set({ dirty: false });
  },

  resetToDefaults: () => {
    const defs = structuredClone(DEFAULT_POLICIES);
    const freshSettings = { ...structuredClone(DEFAULT_SETTINGS), webhookSecret: generateWebhookSecret() };
    if (typeof localStorage !== 'undefined') localStorage.removeItem(POLICIES_KEY);
    set({ policies: defs, settings: freshSettings, dirty: false });
    // 백그라운드 서버 동기화
    const s = get();
    void Promise.all(Object.keys(s.policies).map(apiDelete))
      .then(() => Promise.all(Object.entries(defs).map(([id, p]) => apiPut(id, p))))
      .catch(() => undefined);
  },

  loadFromStorage: () => {
    const stored = readFromStorage();
    if (stored) {
      set({ policies: stored.policies, settings: stored.settings, dirty: false });
    }
  },

  loadFromServer: async () => {
    try {
      const serverPolicies = await apiFetchAll();
      if (Object.keys(serverPolicies).length === 0) {
        // DB가 비어있으면 기본 정책 생성
        const defs = structuredClone(DEFAULT_POLICIES);
        await Promise.all(Object.entries(defs).map(([id, p]) => apiPut(id, p)));
        set({ policies: defs, dirty: false });
      } else {
        set({ policies: serverPolicies, selectedId: Object.keys(serverPolicies)[0] ?? 'default', dirty: false });
      }
    } catch {
      // 실패 시 DEFAULT_POLICIES 유지
    }
  },

  extToString: (arr) => arr.join(','),
  stringToExt: (s) => s.split(',').map((x) => x.trim()).filter(Boolean),
}));
